import { useStore } from "app/stores";
import { FieldArray, Form, Formik } from "formik";
import { observer } from "mobx-react";
import React, { memo, useEffect, useState } from "react";
import { isAdmin, isDoctor, isRoleBranchManager, isRoleCashier, isRoleChiefTechician, isRoleTechician } from "app/common/LocalFunction";
import GlobitsDateTimePicker from "app/common/form/GlobitsDateTimePicker";
import {
  Grid,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Card,
} from "@material-ui/core";
import GlobitsTextField from "app/common/form/GlobitsTextField";
import SaveIcon from "@material-ui/icons/Save";
import BlockIcon from "@material-ui/icons/Block";
import { useTranslation } from "react-i18next";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import { pagingStaff } from "app/services/StaffService";
import GlobitsSelectInput from "app/common/form/GlobitsSelectInput";
import { CONCEPT_TYPE_CODE_VUNG } from "app/common/LocalConst";
import IconDelete from "app/common/Icon/IconDelete";
import { pagingConcept } from "app/services/ConceptService";
import CommonButton from "app/common/Component/Button/CommonButton";
import { UPLOAD_MAXIMUM_IMAGES_TREATMENT_NUMBER } from "app/appConfig";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import {
  getImageVisitEncounterById,
  saveImageVisitEncounter,
} from "app/services/VisitService";
import { uploadFile } from "app/services/FileDescriptionExtService";
import { CloudUpload, Delete } from "@material-ui/icons";
import LightGallary from "app/common/LightGallary";

const EncounterDiagnosisForm = ({ patientId }) => {
  const {
    selectEncounterDiagnosis,
    handleSaveEncounterDiagnosis,
    previewImages,
    setPreviewImages,
    handleGetImageVisitEncounterById,
    selectedCheckin,
    handleDeletSingleImage,
  } = useStore().patientManagementStore.encounterDiagnosisStore;

  const { t } = useTranslation();

  // Khởi tạo giá trị form nếu chưa có data thì tạo mới
  const initialValues =
    selectEncounterDiagnosis
      ? selectEncounterDiagnosis
      : {
        patientId,
        practitioner: null,
        note: "",
        encounterDatetime: selectEncounterDiagnosis?.encounterDatetime,
        listObsDto: [],
      };

  const admin = isAdmin();
  const doctor = isDoctor();
  const cashier = isRoleCashier();
  const branchManager = isRoleBranchManager();
  const techician = isRoleTechician() || isRoleChiefTechician();

  // admin, trường cs, bác sĩ, ktv, thu ngân
  const canEdit = admin || branchManager || doctor || techician || cashier;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSelectImages = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    try {
      // Upload từng file
      const uploadPromises = files.map(async (file) => {
        const uploadRes = await uploadFile(file);
        return uploadRes.data;
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      //truyền vissitid và patientid vào saveImageVisitEncounter
      const saveObj = {
        patientId,
        id: selectedCheckin?.visitId,
        patientFileDescriptions: uploadedFiles.map((file) => ({
          fileDescriptionId: file.id,
        })),
      };

      await saveImageVisitEncounter(saveObj);
      handleGetImageVisitEncounterById(selectedCheckin?.visitId);
    } catch (error) {
      console.error("Lỗi upload ảnh", error);
    } finally {
      const input = document.getElementById("upload-image-preview");
      if (input) input.value = "";
    }
  };

  const handleRemoveImage = (index) => {
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        patientId,
        visitId: selectedCheckin?.visitId,
      }}
      onSubmit={async (values) => {
        await handleSaveEncounterDiagnosis(values);
      }}
      enableReinitialize
    >
      {({ isSubmitting, resetForm, values }) => (
        <Form
          autoComplete="off"
          disabled={!selectedCheckin?.visitId}
          style={isMobile ? { padding: "10px" } : {}}
        >
          <div className="dialog-body">
            <Grid item xs={12}>
              <div className="cards-header flex gap-2 mb-2">
                <h4 style={{ fontSize: "16px" }}>Thông tin chẩn đoán</h4>
              </div>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <GlobitsDateTimePicker
                  label="Ngày khám"
                  name="encounterDatetime"
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <GlobitsPagingAutocomplete
                  api={pagingStaff}
                  name="practitioner"
                  displayLabel="staffName"
                  label="Bác sĩ khám"
                  disabled={!selectedCheckin?.visitId}
                  searchObject={{ isActive: true }}
                />
              </Grid>
              <FieldArray name="listObsDto">
                {({ push, remove }) => (
                  <>
                    <Grid item xs={6}>
                      <GlobitsPagingAutocomplete
                        //label="Vùng Body/Face"
                        label="Vùng"
                        api={pagingConcept}
                        name="concept"
                        searchObject={{
                          conceptTypeCode: CONCEPT_TYPE_CODE_VUNG,
                        }}
                        onChange={(_, value) => {
                          if (value) {
                            push({
                              concept: value,
                              valueCode: "",
                              valueText: "",
                            });
                          }
                        }}
                        disabled={!selectedCheckin?.visitId}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className="table-root mt-2">
                        <div style={{ minWidth: "420px" }}>
                          <table className="table-container">
                            <thead>
                              <tr className="row-table-header">
                                <th width="5%">STT</th>
                                <th width="25%">Vùng</th>
                                <th
                                  style={{ width: isMobile ? "100%" : "20%" }}
                                >
                                  Tình trạng
                                </th>
                                <th>Ghi chú</th>
                                {canEdit && <th>Thao tác</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {values.listObsDto &&
                                values.listObsDto.length > 0 ? (
                                values.listObsDto.map((item, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      {item.concept?.name} -{" "}
                                      {item.concept?.code}
                                    </td>
                                    <td
                                      style={{
                                        width: isMobile ? "100%" : "20%",
                                      }}
                                    >
                                      <GlobitsSelectInput
                                        name={`listObsDto[${index}].valueCoded`}
                                        options={(
                                          item?.concept?.conceptAnswers || []
                                        )
                                          .filter(
                                            (ans) =>
                                              ans?.answerConcept?.code &&
                                              ans?.answerConcept?.name
                                          )
                                          .sort(
                                            (a, b) =>
                                              (a.viewIndex || 0) -
                                              (b.viewIndex || 0)
                                          )
                                          .map((ans) => ({
                                            value: ans.answerConcept.code,
                                            name: ans.answerConcept.name,
                                          }))}
                                      />
                                    </td>
                                    <td
                                      style={{
                                        minWidth: isMobile ? "100px" : "auto",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <GlobitsTextField
                                        name={`listObsDto[${index}].valueText`}
                                        isTextArea
                                      />
                                    </td>
                                    <td align="center">
                                      {canEdit && (
                                        <span
                                          tooltip={"Xóa"}
                                          className="pointer tooltip"
                                          onClick={() => remove(index)}
                                        >
                                          <IconDelete />
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} align="center">
                                    Chưa có vùng nào được chọn
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Grid>
                  </>
                )}
              </FieldArray>
            </Grid>
          </div>
          <div className="dialog-footer">
            <DialogActions className="p-0">
              <div className="flex flex-space-between flex-middle mt-2">
                {canEdit && (
                  <>
                    <CommonButton
                      startIcon={<BlockIcon />}
                      className="btn-add mr-2"
                      onClick={() => resetForm()}
                    >
                      Đặt lại
                    </CommonButton>
                    <Button
                      startIcon={<SaveIcon />}
                      className="mr-0 btn btn-primary d-inline-flex"
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={
                        isSubmitting || !(values?.patientId && values?.visitId)
                      }
                    >
                      {t("general.button.save")}
                    </Button>
                  </>
                )}
              </div>
            </DialogActions>
          </div>
          <Card className="rounded-2xl shadow-sm border mt-4 p-4">
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} md={6}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#1f2937",
                    marginBottom: "4px",
                  }}
                >
                  Danh sách ảnh chẩn đoán
                </div>

                <span
                  style={{
                    fontSize: "14px",
                    color: "#4b5563",
                    fontWeight: 500,
                    lineHeight: "1.5",
                  }}
                >
                  Giới hạn {UPLOAD_MAXIMUM_IMAGES_TREATMENT_NUMBER || 5} ảnh
                  (png, jpg, jpeg)
                </span>
              </Grid>
              {(values?.visitId && canEdit) && (
                <Grid item xs={6} md={6} className="flex justify-end gap-2">
                  <input
                    type="file"
                    id="upload-image-preview"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleSelectImages}
                  />

                  <Button
                    variant="outlined"
                    startIcon={
                      <CloudUpload
                        size={16}
                        style={{
                          color: "#4276a4",
                        }}
                      />
                    }
                    onClick={() => {
                      document.getElementById("upload-image-preview").click();
                    }}
                    className="action-btn-custom"
                  >
                    Tải tệp lên
                  </Button>
                </Grid>
              )}
            </Grid>

            <Grid
              container
              spacing={1}
              className="list-image-by-date"
              style={{ position: "relative" }}
            >
              <ViewListImage
                dataImage={previewImages}
                handleDeletSingleImage={handleDeletSingleImage}
              />
            </Grid>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

const ViewListImage = observer(({ handleDeletSingleImage, dataImage }) => {
  return (
    <>
      <LightGallary>
        {dataImage?.map((item, id) => (
          <Grid
            key={item.id}
            component={"a"}
            md={2}
            xs={6}
            className="lightgallery-item"
            data-src={item?.url}
            data-sub-html={item?.name || ""}
            href={item?.url}
            style={{
              // width: "100%",
              aspectRatio: "1 / 1",
              overflow: "hidden",
              borderRadius: 6,
              cursor: "pointer",
              position: "relative",
            }}
          >
            <img
              src={item.url}
              alt={item.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          </Grid>
        ))}
      </LightGallary>
      <Grid item xs={12} md={12}>
        <div className="overlay-delete-layer">
          <Grid container spacing={1} className="list-image-by-date">
            {dataImage?.map((item, id) => (
              <Grid
                key={item?.id}
                md={2}
                xs={6}
                item
                style={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                }}
              >
                <div
                  className="delete-btn-image"
                  onClick={() => handleDeletSingleImage(item?.id)}
                >
                  <Delete fontSize="small" />
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>
    </>
  );
});
export default memo(observer(EncounterDiagnosisForm));
